/**
 * Tenants page component - Display Tenants with Real API Data
 * Connected to backend API for real-time tenant management
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui';
import { useTenantStatistics } from '@/hooks';
import { tenantService } from '../services/tenant-service';
import { TenantFilters } from '@/components/TenantFilters';
import { TenantTable } from '../components/TenantTable';
import { TenantsEmptyState, TenantsLoadingSkeleton, TenantsErrorState } from '@/components/empty-states/TenantsEmptyStates';
import type { TenantStatus, Tenant, TenantFilterOptions } from '@/types';
import { Badge } from '@/components/ui/badge';


const formatStatusDisplay = (status: TenantStatus) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'INACTIVE':
      return 'Inactive';
    case 'TRIAL_REGISTERED':
      return 'Registered';
    case 'TRIAL_UNREGISTERED':
      return 'Trial';
    case 'MIGRATION':
      return 'Migration';
    case 'ARCHIVED':
      return 'Archived';
    case 'PROVISION':
      return 'Provisioning';
    case 'ERROR':
      return 'Error';
    case 'SUBSCRIBED':
      return 'Subscribed';
    default:
      return status;
  }
};

export function Tenants() {
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(undefined);
  const [pageHistory, setPageHistory] = useState<(string | undefined)[]>([undefined]); // Track page tokens for back navigation
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [filters, setFilters] = useState<TenantFilterOptions>({});
  const itemsPerPage = 25;
  const [viewingTenantId, setViewingTenantId] = useState<string | null>(null);
  const [viewingTenant, setViewingTenant] = useState<any | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  

  // Use tenantService.getTenantsAccumulate for paginated tenant data
  const [tenants, setTenants] = useState<any[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string|undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const { data: stats, isLoading: statsLoading, error: statsError } = useTenantStatistics(filters);

  // Fetch tenants recursively to fill each page
  React.useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await tenantService.getTenantsAccumulate({ limit: itemsPerPage, nextToken: currentPageToken }, filters);
        if (!cancelled) {
          setTenants(resp.items || []);
          setHasNextPage(resp.hasMore);
          setNextPageToken(resp.nextToken);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [currentPageToken, filters]);
  React.useEffect(() => {
    let cancelled = false;
    async function loadTenant() {
      if (!viewingTenantId) return;
      setViewLoading(true);
      try {
        const data = await tenantService.getTenant(viewingTenantId);
        if (!cancelled) setViewingTenant(data);
      } catch (e) {
        if (!cancelled) setViewingTenant(null);
      } finally {
        if (!cancelled) setViewLoading(false);
      }
    }
    loadTenant();
    return () => { cancelled = true; };
  }, [viewingTenantId]);



  const handleFiltersChange = (newFilters: TenantFilterOptions) => {
    setFilters(newFilters);
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 0) {
      const prevIndex = currentPageIndex - 1;
      setCurrentPageIndex(prevIndex);
      setCurrentPageToken(pageHistory[prevIndex]);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage && nextPageToken) {
      const newIndex = currentPageIndex + 1;
      // Add the next page token to history if we haven't seen this page before
      if (pageHistory.length <= newIndex) {
        setPageHistory([...pageHistory, nextPageToken]);
      }
      setCurrentPageIndex(newIndex);
      setCurrentPageToken(nextPageToken);
    }
  };

  // No need for client-side pagination anymore - tenants is already paginated from backend
  // const paginatedTenants = tenants; // Remove this line entirely

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">Tenant Management</h1>
          <p className="content-subtitle">View and manage all tenants in your multi-tenant SaaS platform</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">Tenant Management</h1>
          <p className="content-subtitle">View and manage all tenants in your multi-tenant SaaS platform</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-center py-8">
            <p className="text-red-600">Error loading tenants</p>
            <p className="text-sm text-gray-500 mt-2">
              {error instanceof Error ? error.message : 'Unable to load tenant data'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content Header */}
      <div className="content-header flex-shrink-0">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="content-title">Tenant Management</h1>
            <p className="content-subtitle">View and manage all tenants in your multi-tenant SaaS platform</p>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="stats-overview flex-shrink-0">
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.total || 0)}
          </div>
          <div className="stat-label">Total Tenants</div>
        </div>
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.active || 0)}
          </div>
          <div className="stat-label">Active</div>
        </div>
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.trial || 0)}
          </div>
          <div className="stat-label">Trial</div>
        </div>
        <div className="stat-item">
          <div className={`stat-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.inactive || 0)}
          </div>
          <div className="stat-label">Inactive</div>
        </div>
      </div>

      {/* Tenant Filters */}
      <TenantFilters
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Filter Status Indicator */}
      {Object.values(filters).some(value => 
        value !== undefined && value !== null && value !== '' && 
        (Array.isArray(value) ? value.length > 0 : true)
      ) && (
        <div className="mb-4">
          <div className="text-sm text-blue-600 font-medium">
            Filters Active
            {(() => {
              const activeFilters = [];
              if (filters.name) activeFilters.push(`Name: "${filters.name}"`);
              if (filters.status) activeFilters.push(`Status: ${filters.status}`);
              if (filters.minLicenses !== undefined) activeFilters.push(`Min Licenses: ${filters.minLicenses}`);
              if (filters.maxLicenses !== undefined) activeFilters.push(`Max Licenses: ${filters.maxLicenses}`);
              
              return activeFilters.length > 0 ? ` (${activeFilters.join(', ')})` : '';
            })()}
          </div>
        </div>
      )}

      {/* Tenant Table Card */}
      <div className="card">
        {isLoading ? (
          <TenantsLoadingSkeleton />
        ) : error ? (
          <TenantsErrorState 
            onRetry={() => window.location.reload()}
            error={typeof error === 'string' ? error : 'Failed to load tenants'}
          />
        ) : tenants.length === 0 ? (
          <TenantsEmptyState 
            hasFilters={Object.values(filters).some(value => 
              value !== undefined && value !== null && value !== '' && 
              (Array.isArray(value) ? value.length > 0 : true)
            )}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <div className="table-container">
            <div className="table-scroll-wrapper">
              <TenantTable
                tenants={tenants as Tenant[]}
                onView={(tenantId) => setViewingTenantId(tenantId)}
                isLoading={isLoading}
                startIndex={itemsPerPage * currentPageIndex}
              />
            </div>

            {/* Pagination */}
            <div className="table-pagination">
              <div className="pagination-info">
                {tenants.length > 0
                  ? `Showing ${itemsPerPage * currentPageIndex + 1} to ${itemsPerPage * currentPageIndex + tenants.length} entries (page ${currentPageIndex + 1})`
                  : 'No entries'}
              </div>
              <div className="pagination-controls">
                <button 
                  className="access-btn access-secondary" 
                  onClick={handlePreviousPage}
                  disabled={currentPageIndex === 0}
                >
                  Previous
                </button>
                <button 
                  className="access-btn access-secondary"
                  onClick={handleNextPage}
                  disabled={!hasNextPage}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
    </div>

    {viewingTenantId && (
      <div className="modal-overlay" onClick={() => setViewingTenantId(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <h3 className="modal-title">Tenant Details</h3>
          {viewLoading ? (
            <div className="py-4">Loading...</div>
          ) : viewingTenant ? (
            <div className="space-y-2">
              <div><strong>ID:</strong> {viewingTenant.id}</div>
              <div><strong>Company:</strong> {viewingTenant.company}</div>
              <div>
                <strong>Creators:</strong>{' '}
                {Array.isArray(viewingTenant.creators) && viewingTenant.creators.length > 0 ? (
                  <span>{viewingTenant.creators[0]?.name} ({viewingTenant.creators[0]?.email})</span>
                ) : (
                  <span>-</span>
                )}
              </div>
              <div><strong>Company Size:</strong> {viewingTenant.companySize}</div>
              <div><strong>Deployment ID:</strong> {viewingTenant.deploymentId}</div>
              <div><strong>Number Of Licenses:</strong> {viewingTenant.numberOfLicenses}</div>
              <div><strong>Status:</strong> {viewingTenant.status}</div>
              <div><strong>Last Modified:</strong> {viewingTenant.lastModified}</div>
              <div><strong>Inserted:</strong> {viewingTenant.insertTimestamp}</div>
            </div>
          ) : (
            <div className="py-4 text-red-600">Failed to load tenant details.</div>
          )}
          <div className="modal-actions mt-4">
            <button className="access-btn access-secondary" onClick={() => setViewingTenantId(null)}>Close</button>
          </div>
        </div>
      </div>
    )}
  </div>
  );
}
